import { CartUpdateEvent } from '@theme/events';

/**
 * Keeps Starter Pack lines and their "Firefly Refill Plan" placeholder lines in
 * lockstep, on every cart surface.
 *
 * WHY THIS IS NOT OPTIONAL
 * The placeholder is a real, non-zero product ($49–$184). The
 * firefly-tiered-pricing Function only discounts it to zero while it pairs 1:1
 * with an unconsumed pack of the MATCHING tier in the same cart — that pairing
 * requirement is the security control that stops anyone adding a free
 * subscription on its own. So an orphaned placeholder is not a cosmetic bug: it
 * silently charges the customer full price for a stub line.
 *
 * WHY IT IS A NETWORK GUARD RATHER THAN CART UI CODE
 * The live drawer is Rebuy Smart Cart, whose markup and remove buttons the
 * theme does not own. Watching cart mutations at the fetch/XHR layer covers
 * Rebuy, the theme's native drawer, the cart page, and anything else that talks
 * to the Ajax API, with one implementation.
 *
 * INERT BY DEFAULT: every pass short-circuits unless the cart actually contains
 * a mapped placeholder variant, so ordinary traffic pays one /cart.js read per
 * cart mutation and nothing else.
 */

// Pack variant -> placeholder variant. Mirrors REFILL_PLANS in
// cart-transform/extensions/firefly-tiered-pricing/src/cart_lines_discounts_generate_run.js
// and the "Pack variant → placeholder variant" setting on ct-refill-plan-toggle.
// All three must agree; the Function is the authority.
const PAIRS = {
  43185789730877: 43425015726141, // Base      / 1 device
  40965511053373: 43425015758909, // Original  / 2 devices
  40965511086141: 43425015791677, // Elite     / 3 devices
  41818331086909: 43425015824445, // Ultimate  / 4 devices
};

/** placeholder variant id -> pack variant id */
const PACK_FOR = Object.fromEntries(Object.entries(PAIRS).map(([pack, placeholder]) => [placeholder, Number(pack)]));

const root = () => window.Shopify?.routes?.root || '/';

/**
 * Line item property that ties a pack line to the placeholder added with it.
 * Must match PAIR_KEY in ct-refill-plan-toggle.js.
 *
 * Lines added before this existed carry no token; they fall into the shared
 * '' bucket and pair exactly as they did before, so carts created by the old
 * build keep working.
 */
const PAIR_KEY = '_ct_sub';

/** @param {{properties?: Record<string, string> | null}} line */
const tokenOf = (line) => line.properties?.[PAIR_KEY] ?? '';

/** Set while the guard's own corrections are in flight, so it never re-enters. */
let working = false;

/** A cart change observed while corrections were running; replayed afterwards. */
let missed = false;

/** @type {number | undefined} */
let timer;

/**
 * @param {string} path
 * @param {object} [body]
 */
async function cartRequest(path, body) {
  const response = await fetch(`${root()}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw new Error(`${path} -> ${response.status}`);
  return response.json();
}

/**
 * Bring every placeholder line to the quantity of the pack it is paired with.
 *
 * Removing a pack drives its placeholder to 0; changing a pack's quantity
 * mirrors onto the placeholder. A placeholder the customer removed on its own
 * is simply absent, so nothing is ever re-added — dropping the subscription
 * while keeping the pack stays a valid choice.
 *
 * PAIRING IS BY TOKEN, NOT BY VARIANT
 * A pack line carries no selling plan — only the placeholder does — so two
 * lines of the same pack variant are identical in the cart whether they were
 * bought one-time or on subscription. Matching on variant id alone therefore
 * summed BOTH into the placeholder's quantity, and adding a one-time pack
 * silently raised the customer's recurring charge (Ultimate: $156.40/mo ->
 * $469.20/mo on two one-time adds). The same collision made a second cadence
 * for one pack size delete the first, moving a 3-monthly subscriber onto
 * monthly at double quantity. Both were reported in QA on 2026-09-16.
 *
 * The token scopes each pack's quantity to the placeholder it was added with,
 * so unrelated lines of the same variant no longer feed each other.
 */
async function reconcile() {
  if (working) {
    missed = true;
    return;
  }

  let cart;
  try {
    cart = await cartRequest('cart.js');
  } catch {
    return;
  }

  const lines = cart?.items ?? [];
  if (!lines.some((line) => PACK_FOR[line.variant_id] != null)) return;

  /** @type {Map<string, number>} `${pack variant id}::${token}` -> quantity available for pairing */
  const packQuantities = new Map();
  for (const line of lines) {
    if (PAIRS[line.variant_id] == null) continue;
    const bucket = `${line.variant_id}::${tokenOf(line)}`;
    packQuantities.set(bucket, (packQuantities.get(bucket) ?? 0) + line.quantity);
  }

  /** @type {{key: string, quantity: number}[]} */
  const corrections = [];
  for (const line of lines) {
    const pack = PACK_FOR[line.variant_id];
    if (pack == null) continue;

    // Claims only the packs added alongside THIS placeholder. A second
    // placeholder for the same pack size carries its own token and draws on
    // its own pack line, so distinct cadences now coexist instead of the
    // first one deleting the second. Within a bucket the 1:1 rule still
    // holds: the first claimant takes it all, so a duplicate placeholder
    // sharing a token still drops to 0 rather than being charged for.
    const bucket = `${pack}::${tokenOf(line)}`;
    const available = packQuantities.get(bucket) ?? 0;
    packQuantities.set(bucket, 0);

    if (line.quantity !== available) corrections.push({ key: line.key, quantity: available });
  }

  if (!corrections.length) return;

  working = true;
  missed = false;
  let updated;
  try {
    for (const correction of corrections) {
      // Sequential: each change returns a fresh cart and can shift other keys.
      updated = await cartRequest('cart/change.js', { id: correction.key, quantity: correction.quantity });
    }
  } catch (error) {
    console.error('[ct-refill-plan-guard]', error);
    return;
  } finally {
    working = false;
  }

  // No `sections` in the payload, so cart-items-component re-renders its
  // section from the server rather than morphing stale HTML. itemCount must be
  // the absolute count: cart-icon writes it straight into the bubble.
  document.dispatchEvent(
    new CartUpdateEvent({}, 'ct-refill-plan-guard', {
      source: 'ct-refill-plan-guard',
      itemCount: updated?.item_count,
    })
  );

  const smartCart = /** @type {any} */ (window).Rebuy?.SmartCart;
  for (const method of ['refresh', 'reloadCart', 'update']) {
    if (typeof smartCart?.[method] === 'function') {
      smartCart[method]();
      break;
    }
  }

  if (missed) schedule();
}

function schedule() {
  clearTimeout(timer);
  // Long enough to land after the UI's own change request settles, short
  // enough that an orphan is never visible for a beat.
  timer = setTimeout(reconcile, 250);
}

/** Any Ajax API call that can change line quantities. */
const MUTATES = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

// Loaded from both snippets/scripts.liquid and the PDP block. ES modules
// evaluate once per URL so this should never double-fire, but patching fetch
// twice would be bad enough to be worth an explicit latch.
if (window.__ctRefillPlanGuardInstalled) {
  console.debug('[ct-refill-plan-guard] already installed, skipping');
} else {
  window.__ctRefillPlanGuardInstalled = true;

  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    // The caller's promise is returned untouched; the guard only attaches a
    // second, separately-caught branch, so a failure here can never change
    // what a cart request resolves or rejects with.
    const result = nativeFetch.apply(window, args);
    try {
      const input = args[0];
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input?.url ?? '');
      if (MUTATES.test(url)) result.then(schedule, () => {});
    } catch {
      /* never let the guard break a cart request */
    }
    return result;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    try {
      if (MUTATES.test(String(url))) this.addEventListener('load', schedule, { once: true });
    } catch {
      /* never let the guard break an XHR */
    }
    return nativeOpen.call(this, method, url, ...rest);
  };

  document.addEventListener('rebuy:cart.change', schedule);

  // Catches anything the interceptors missed — a cart mutated in another tab, a
  // bfcache restore, or an app that changes the cart server-side.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  } else {
    reconcile();
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reconcile();
  });
}
